// slotUtils.ts — Shared slot utilities for click-to-place feature placement
import type { Slot, ActiveDialog } from '../types/panelvar';

/** Returns the index of the first disabled slot, or -1 if all full. */
export function findNextSlot<T>(slots: Slot<T>[]): number {
  return slots.findIndex(s => !s.enabled);
}

/** Generate the next auto-mark for a slot group (e.g. "RO-3"). */
export function autoMark(prefix: string, slots: Slot<{ mark: string }>[]): string {
  const n = slots.filter(s => s.enabled).length + 1;
  return `${prefix}-${n}`;
}

/** Map from slot group key → ActiveDialog name. */
const GROUP_DIALOG_MAP: Record<string, ActiveDialog> = {
  rovar: 'rough-openings',
  wdvar: 'windows',
  drvar: 'man-doors',
  dlvar: 'dock-levelers',
  sbvar: 'rect-blockouts',
  rbvar: 'round-blockouts',
  fhvar: 'horiz-features',
  fvvar: 'vert-features',
  plvar: 'pilasters',
  bpvar: 'brace-points',
  ppvar: 'pick-points',
  wcvar: 'weld-connections',
};

export function groupToDialog(group: string): ActiveDialog | null {
  return GROUP_DIALOG_MAP[group] ?? null;
}

/** Map from slot group key → human-readable mark prefix. */
const GROUP_PREFIX: Record<string, string> = {
  rovar: 'RO',
  wdvar: 'WD',
  drvar: 'DR',
  dlvar: 'DL',
  sbvar: 'BO',
  rbvar: 'RB',
  fhvar: 'FH',
  fvvar: 'FV',
  plvar: 'PL',
  bpvar: 'BP',
  ppvar: 'PP',
  wcvar: 'WC',
};

export function groupPrefix(group: string): string {
  return GROUP_PREFIX[group] ?? group.toUpperCase();
}

/** Slot group keys that support click-to-place. */
export type SlotGroupKey = keyof typeof GROUP_DIALOG_MAP;

export const SLOT_GROUPS = Object.keys(GROUP_DIALOG_MAP) as SlotGroupKey[];
